
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { runFullStressDiagnostics, exportSystemAuditReport } from '../../services/testSuite';
import { TestResult } from '../../types';
import { useGuitarMatch } from '../../context/GuitarMatchContext';
import { StressResult } from '../../services/stressSuite';
import { validateAuditV2_1, CONTRACT_VERSION } from '../../services/auditContract';
import { buildRepairMap, RepairItem } from '../../services/repairMapBuilder';

export const DiagnosticPanel: React.FC = () => {
    const { result, setActiveOverlay, setShowSupportHub } = useGuitarMatch();
    const [isRunning, setIsRunning] = useState(false);
    const [results, setResults] = useState<TestResult[]>([]);
    const [stressResults, setStressResults] = useState<StressResult[]>([]);
    const [repairMap, setRepairMap] = useState<RepairItem[]>([]);
    const [logs, setLogs] = useState<string[]>(["[SYS] GSC V2.1 Industrial Engine Ready"]);
    const [auditStatus, setAuditStatus] = useState<any>(null);
    
    const logEndRef = useRef<HTMLDivElement>(null);
    const addLog = useCallback((msg: string) => {
        setLogs(prev => [...prev.slice(-50), `[${new Date().toLocaleTimeString()}] ${msg}`]);
    }, []);

    const runAudit = async () => {
        setIsRunning(true);
        setAuditStatus(null);
        addLog("STARTING INDUSTRIAL AUDIT V2.1 (A-D)...");
        try {
            const data = await runFullStressDiagnostics();
            setResults(data.diagnostics);
            setStressResults(data.stress);
            
            const reportStub = { 
                meta: { contract_version: CONTRACT_VERSION },
                diagnostics: data.diagnostics, 
                stress: data.stress 
            };
            const val = validateAuditV2_1(reportStub);
            const repairs = buildRepairMap(reportStub);
            
            setAuditStatus(val);
            setRepairMap(repairs);
            
            addLog(`AUDIT COMPLETE. Valid: ${val.valid}. ${repairs.length} repair items found.`);
        } catch (e: any) {
            addLog(`CRITICAL ERROR: ${e.message}`);
        } finally {
            setIsRunning(false);
        }
    };

    const handleOpenTuner = () => {
        setShowSupportHub(false);
        setActiveOverlay('GOD_MODE');
    };

    useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logs]);

    const TierBadge = ({ label, passed }: { label: string, passed: boolean }) => (
        <div className={`px-4 py-2 rounded-xl border flex items-center gap-3 transition-all ${passed ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
            <span className="text-[10px] font-black uppercase tracking-widest">Tier {label}</span>
            <span className="text-xs">{passed ? '✓' : '✕'}</span>
        </div>
    );

    return (
        <div className="space-y-8 animate-fade-in font-mono pb-20">
            {/* TERMINAL */}
            <div className="bg-black rounded-[32px] border border-white/10 p-6">
                <div className="flex justify-between items-center mb-4">
                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">System Terminal v2.1</div>
                    <button 
                        onClick={handleOpenTuner}
                        className="text-[9px] font-black text-smart-gold uppercase tracking-widest border border-smart-gold/30 px-3 py-1 rounded-lg hover:bg-smart-gold hover:text-black transition-all"
                    >
                        Accéder aux Sliders 🎛️
                    </button>
                </div>
                <div className="h-40 overflow-y-auto text-[10px] space-y-1 text-emerald-500 custom-scrollbar">
                    {logs.map((log, i) => (
  <div key={i} className="whitespace-nowrap">
    {'>'} {log}
  </div>
))}

                    <div ref={logEndRef} />
                </div>
            </div>

            {/* STATUS TIERS */}
            {auditStatus && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up">
                    <TierBadge label="A" passed={auditStatus.tierPasses.A} />
                    <TierBadge label="B" passed={auditStatus.tierPasses.B} />
                    <TierBadge label="C" passed={auditStatus.tierPasses.C} />
                    <TierBadge label="D" passed={auditStatus.tierPasses.D} />
                </div>
            )}

            {/* REPAIR MAP */}
            {repairMap.length > 0 && (
                <div className="bg-black/40 rounded-[32px] border border-white/5 overflow-hidden animate-fade-in-up">
                    <header className="p-6 border-b border-white/5 bg-white/5 flex justify-between items-center">
                        <h4 className="text-[10px] font-black text-white uppercase tracking-[0.4em]">Automated Repair Map</h4>
                        <span className="text-[8px] text-slate-500 uppercase font-bold">{repairMap.length} Actions</span>
                    </header>
                    <div className="divide-y divide-white/5">
                        {repairMap.map((item) => (
                            <div key={item.id} className="p-6 hover:bg-white/[0.02] transition-all group">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-3">
                                        <span className={`px-2 py-0.5 rounded text-[8px] font-black ${item.priority === 'P0' ? 'bg-red-500 text-white' : 'bg-smart-gold text-black'}`}>{item.priority}</span>
                                        <h5 className="text-sm font-black text-white uppercase italic">{item.area}</h5>
                                    </div>
                                    <span className="text-[9px] text-slate-500 font-mono">{item.id}</span>
                                </div>
                                <p className="text-[11px] text-slate-300 italic mb-4">"{item.symptom}"</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                                        <span className="text-[8px] text-slate-500 uppercase font-black block mb-1">Owner</span>
                                        <span className="text-10px text-cyan-400 font-bold">{item.owner_hint}</span>
                                    </div>
                                    <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                                        <span className="text-[8px] text-slate-500 uppercase font-black block mb-1">Fix Instruction</span>
                                        <span className="text-10px text-white">{item.fix_hint}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* RESULTS TABLE */}
            <div className="bg-black/40 rounded-[32px] border border-white/5 overflow-hidden">
                <table className="w-full text-left text-[11px]">
                    <thead className="bg-white/5 uppercase">
                        <tr>
                            <th className="p-4 text-slate-500">Tier</th>
                            <th className="p-4 text-slate-500">Module</th>
                            <th className="p-4 text-slate-500">Test</th>
                            <th className="p-4 text-slate-500">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {stressResults.map((r, i) => (
                            <tr key={i} className="hover:bg-white/[0.02]">
                                <td className="p-4 text-slate-600 font-bold">{r.tier}</td>
                                <td className="p-4 text-cyan-400 font-bold">{r.module}</td>
                                <td className="p-4 text-white uppercase truncate max-w-[150px]">{r.name}</td>
                                <td className="p-4">
                                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black ${r.passed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                        {r.passed ? 'PASS' : 'FAIL'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex gap-4">
                <button onClick={runAudit} disabled={isRunning} className="flex-[2] py-6 bg-white text-black rounded-2xl font-black uppercase text-xs hover:scale-[1.02] transition-all disabled:opacity-30">
                    {isRunning ? 'RUNNING V2.1 SUITE...' : 'LANCER AUDIT INDUSTRIEL V2.1 🧪'}
                </button>
                <button 
                    onClick={() => exportSystemAuditReport(results, stressResults, result)} 
                    disabled={isRunning || !auditStatus} 
                    className={`flex-1 border rounded-2xl font-black uppercase text-[10px] transition-all ${
                        auditStatus?.valid 
                        ? 'border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-black' 
                        : auditStatus 
                            ? 'border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black'
                            : 'border-white/10 text-slate-600 opacity-50 cursor-not-allowed'
                    }`}
                >
                    {auditStatus?.valid ? 'Exporter JSON ✓' : auditStatus ? 'Exporter DEBUG ⚠️' : 'Exporter JSON'}
                </button>
            </div>
        </div>
    );
};
